
export type Language = 'en' | 'fr';

export const translations = {
  en: {
    nav: {
      mission: "Mission Control",
      useCases: "Use Cases",
      intelligence: "Intelligence Hub",
      verification: "Verification APIs",
      nodes: "Global Nodes",
      contact: "Contact",
      signIn: "Sign In",
      getStarted: "Get Started"
    },
    hero: {
      tagline: "Global RegTech & Risk Intelligence Platform",
      title1: "Secure. Sovereign.",
      title2: "Intelligent.",
      desc: "Minerva Regtech unifies identity verification, corporate intelligence, blockchain forensics, and operational risk into a modular, enterprise-grade ecosystem.",
      init: "Initialize Platform",
      contactHub: "Contact Integration Hub"
    },
    narrative: {
      title: "Global RegTech",
      subtitle: "& Cyber Intelligence.",
      desc: "Minerva RegTech™ is a regulatory technology firm focused on strengthening compliance, financial crime prevention, and institutional integrity across global financial and legal markets.",
      unifiedStack: "Unified Stack",
      unifiedStackDesc: "Identity, Forensics, and ESG monitoring in one modular portal.",
      instantScale: "Instant Scale",
      instantScaleDesc: "Cloud-native nodes designed for institutional throughput."
    },
    portfolio: {
      title: "Our Portfolio — Trusted by Global Institutions"
    },
    risk: {
      tagline: "Risk Landscape 2024",
      title: "360° Compliance Oversight.",
      desc: "Institutions today face increasing pressure from evolving sanctions, synthetic identity threats, and complex global UBO structures.",
      kyc: "Cross-border KYC/KYB",
      kycDesc: "Complex multijurisdictional compliance challenges handled at source.",
      ubo: "UBO & Shareholding",
      uboDesc: "Unmasking complex beneficial ownership and opaque shell structures.",
      sanctions: "Dynamic Sanctions",
      sanctionsDesc: "Rapidly evolving frameworks requiring real-time list synchronization.",
      crypto: "Crypto-asset AML",
      cryptoDesc: "Full visibility into virtual asset exposure and high-risk on-ramps.",
      esg: "ESG Obligations",
      esgDesc: "Automating mandatory sustainability reporting and adverse media monitoring.",
      fraud: "Real-time Fraud",
      fraudDesc: "Countering SIM-swap, synthetic identity, and credential leak threats.",
      audit: "Oversight & Audit",
      auditDesc: "Expanding institutional surveillance requirements and regulator audits.",
      sentiment: "Sentiment & Geopolitical",
      sentimentDesc: "Managing risks impacting operational decisions and brand reputation."
    },
    about: {
      protocol: "The Minerva Regtech Protocol",
      title: "Vision & Mission.",
      desc1: "Minerva RegTech™ is a regulatory technology firm focused on strengthening compliance, financial crime prevention, and institutional integrity across global financial and legal markets.",
      desc2: "As regulatory expectations evolve and financial ecosystems become more interconnected, organisations require compliance frameworks that are intelligent, scalable, and defensible.",
      cta: "Full Mission Control",
      partner: "Your best partner",
      returnHome: "Return Home",
      missionControl: "Mission Control",
      origin: "Auckland, New Zealand",
      standard: "The Institutional Standard",
      secureSovereign: "Secure. Sovereign. Intelligent.",
      standardDesc1: "We work with Fintech companies, banks, private equity and venture capital firms, stock market participants, regulators, law firms, and family offices to address complex regulatory challenges with clarity and confidence.",
      standardDesc2: "Our solutions integrate advanced identity verification, anti-financial crime technologies, and efficient compliance systems designed to protect operations while supporting sustainable growth.",
      statHq: "HQs",
      statNetwork: "Network",
      statNodes: "14 Sovereign Nodes",
      statIntel: "Intelligence",
      statNeural: "AI Neural Verification",
      statCompliance: "Compliance",
      statStandard: "AML4/5D Standard",
      architecture: "The Architecture",
      pillars: "Strategic Pillars.",
      pillar1Title: "Unified Stack",
      pillar1Desc: "Unifying identity verification, corporate intelligence, and ESG risk into a single modular portal for instant institutional oversight.",
      pillar2Title: "Real-time Forensics",
      pillar2Desc: "Continuous blockchain surveillance and darkweb leak detection ensuring your perimeter is defended against emerging financial crimes.",
      pillar3Title: "Sovereign Data",
      pillar3Desc: "Regional data residency nodes across 5 continents, ensuring multijurisdictional compliance and absolute data sovereignty.",
      ourMission: "Our Mission",
      ourMissionDesc: "As regulatory expectations evolve and financial ecosystems become more interconnected, organisations require compliance frameworks that are intelligent, scalable, and defensible. Minerva RegTech™ delivers technology-driven solutions that enhance transparency, mitigate risk, and reinforce trust.",
      ourVision: "Our Vision",
      ourVisionDesc: "To pioneer a future where regulatory compliance is a strategic advantage. We envision a global financial ecosystem where innovation and growth never compromise integrity, security, or social responsibility.",
      originBadge: "Global Compliance Engineering • New Zealand Origin",
      originDesc: "Minerva RegTech is incorporated in Auckland and maintains operational nodes in London, Bangkok, New York, and Lomé.",
      initPlatform: "Initialize Platform",
      rights: "© 2023 Minerva Regtech"
    },
    services: {
      title: "Intelligence Nodes.",
      desc: "Traditional compliance stacks are fragmented and costly. Minerva Regtech unifies identity, corporate intelligence, forensics, and operational verification into a single portal.",
      ip: "IP Intelligence",
      ipDesc: "Real-time IP profiling with geolocation, ASN intelligence, and proxy/VPN/TOR detection to identify fraud.",
      kyc: "e-KYC / KYB",
      kycDesc: "Automated UBO mapping and structural registry validation across.",
      aml: "AML Sentinel",
      amlDesc: "Sanctions screening and transaction monitoring against real-time global watchlists.",
      lei: "Legal Entity Identifier Registry",
      leiDesc: "GLEIF-compliant LEI management, issuance, and renewal through our dedicated node.",
      blockchain: "Blockchain Intelligence",
      blockchainDesc: "Deep-chain risk scoring and wallet nexus analysis powered by Blockchain Intelligence™.",
      esg: "ESG Due Dilligence",
      esgDesc: "Sustainability intelligence and adverse media tracking.",
      market: "Market Sentiment",
      marketDesc: "Social perception monitoring and news outlier detection.",
      darkweb: "Darkweb Surveillance",
      darkwebDesc: "24/7 scanning of onion nodes for leaked credentials and corporate data dumps.",
      explore: "Explore Module"
    },
    useCases: {
      tagline: "Industry Applications",
      title: "Tailored for High-Stakes.",
      desc: "From global banking conglomerates to specialised law enforcement units, Minerva provides the intelligence nodes required to navigate complex regulatory landscapes.",
      banking: "Banking & Finance",
      regulators: "Regulators & Law enforcement",
      fintech: "FinTech & DeFi",
      pe: "Private Equity & venture Capital",
      cta: "View All Use Cases",
      returnHome: "Return Home",
      titlePage: "Use Cases",
      institutionalApps: "Institutional Applications",
      industrySolutions: "Industry Solutions.",
      solutionsDesc: "Minerva RegTech™ provides specialised intelligence nodes tailored for the world's most demanding financial and regulatory environments.",
      ready: "Ready to deploy?",
      readyDesc: "Join the institutions already using Minerva Regtech to secure their operations and automate compliance.",
      init: "Initialise Platform",
      banks: "Banks",
      banksDesc: "Fraud detection via Threat Intelligence, real-time sanctions screening, AML/CTF monitoring, ESG Due Diligence (including supply chain), operational intelligence (Phone, IBAN, Email), HR/employee background checks, IP/Threat Intelligence monitoring, and Darkweb surveillance.",
      pevc: "Private Equity & Venture Capital (PE/VC)",
      pevcDesc: "UBO mapping, ESG Due Diligence (including supply chain), Blockchain Intelligence, market sentiment & geopolitical risk analysis, compliance dashboards, executive verification, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for portfolio evaluation.",
      law: "Law Enforcement & Regulators",
      lawDesc: "Cross-border sanctions enforcement, forensic investigations, corporate intelligence, market monitoring, employee verification, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for suspect verification.",
      stocks: "Stock Markets & Exchanges",
      stocksDesc: "Real-time surveillance of participants, transaction monitoring, market sentiment analysis, compliance verification, Blockchain Intelligence, employee checks, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for listed companies’ executives.",
      fintechDefi: "FinTech & DeFi Platforms",
      fintechDefiDesc: "Onboarding verification, crypto-asset AML, wallet risk scoring, sanctions/PEP screening, ESG Due Diligence (including supply chain), operational intelligence (Phone, IBAN, Email), employee checks, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for users and partners.",
      familyOffices: "Family Offices",
      familyOfficesDesc: "UBO/shareholding mapping, adverse media monitoring, ESG Due Diligence (including supply chain), market & geopolitical intelligence, counterparty verification, employee checks, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for portfolio oversight.",
      riskIntel: "Market, Regulatory & Geopolitical Risk Intelligence",
      riskIntelDesc: "Social sentiment & geopolitical monitoring, early-warning alerts, cross-referencing corporate intelligence, Blockchain Intelligence, ESG signals, operational intelligence, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for actionable intelligence reporting."
    },
    verification: {
      tagline: "Real-time Gateway",
      title: "Verification Micro-services.",
      desc: "Modular APIs for instant contact and financial verification, designed to be integrated into any onboarding flow within minutes.",
      iban: "IBAN Validation",
      ibanDesc: "Confirmation of Payee (CoP) and SEPA reachability.",
      phone: "Phone Intelligence",
      phoneDesc: "SIM-swap detection and global SS7 signal audit.",
      email: "Email Mailbox Forensics",
      emailDesc: "SMTP handshake verification and DLP leak checks.",
      status: "Operational",
      cta: "Request API Keys"
    },
    reach: {
      tagline: "Jurisdictional Network",
      title: "Global Infrastructure.",
      desc: "Localized data residency and high-speed reporting nodes across 14 sovereign regions and 5 continents."
    },
    contact: {
      successTitle: "Request Transmitted.",
      successDesc: "An institutional compliance lead has been alerted. Response windows average 240 seconds.",
      successCta: "New Transmission",
      tagline: "Secure Integration Support",
      title: "Contact us.",
      desc: "Request private beta access or dedicated node hosting.",
      name: "Full Name",
      email: "Business Email",
      message: "Message",
      messagePlaceholder: "Requirements, region of interest...",
      cta: "Secure Send",
      hq: "Headquarters",
      returnHome: "Return to Home",
      hub: "Hub",
      restricted: "Restricted Access Protocol",
      calibration: "Portal Calibration.",
      handshake: "The Handshake.",
      demoDesc: "Our institutional intelligence portal is currently under final regulatory calibration. To explore our full capabilities or request priority access, please book an institutional demo below.",
      contactDesc: "Request private beta access, institution-wide deployment, or dedicated node hosting.",
      lead: "Institutional Lead (Name)",
      org: "Organization",
      objective: "Objective",
      demo: "Request Product Demo",
      enterprise: "Enterprise Implementation",
      quota: "API Quota Expansion",
      partnership: "Regulator Partnership",
      dossier: "Dossier Brief",
      dossierPlaceholder: "Specify requirements, region of interest, and target data residency...",
      initBooking: "Initialize Booking",
      globalInfra: "Global Infrastructure",
      priority: "Priority Line",
      priorityDesc: "Institutional security breaches or urgent sanctions nexus require direct dispatch.",
      verifyRegistry: "Verify Registry"
    },
    footer: {
      desc: "Secure, transparent, and high-performance infrastructure for institutional regulatory compliance.",
      resources: "Resources",
      mission: "Our Mission",
      useCases: "Use Cases",
      support: "Contact Support",
      register: "Register Account",
      connect: "Connect",
      rights: "© 2024 Minerva Regtech. All Rights Reserved.",
      privacy: "Privacy Policy"
    },
    sidebar: {
      dashboard: "Dashboard",
      regulatory: "Regulatory (AML/LEI)",
      aml: "AML Monitoring",
      lei: "LEI Registry",
      kyc: "Individual KYC",
      kyb: "KYB (Corporate)",
      fintech: "Fintech Intel",
      blockchain: "Blockchain Intel",
      iban: "IBAN Monitoring",
      market: "Stock Market",
      intelligence: "Intelligence Services",
      esg: "ESG Risk Score",
      darkweb: "Darkweb Monitor",
      sentiment: "Market Sentiment",
      verification: "Verification APIs",
      phone: "Phone Intelligence",
      email: "Email Validation",
      support: "Support",
      contact: "Contact Hub",
      settings: "Settings",
      logout: "Logout",
      plan: "Plan"
    },
    dashboard: {
      searchPlaceholder: "Search entities, ISINs or LEIs...",
      officer: "Officer",
      admin: "Admin",
      role: "Senior Compliance",
      title: "Intelligence Dashboard",
      subtitle: "Real-time risk and compliance telemetry.",
      apiUsage: "API Usage",
      auditExport: "Audit Export",
      marketMonitor: "Market Monitor",
      activeIsins: "Active ISINs",
      viewTerminal: "View Terminal",
      blockchainIntel: "Blockchain Intel",
      addresses: "Addresses",
      forensics: "Forensics",
      darkwebLeak: "Darkweb Leak",
      leaksFound: "Leaks Found",
      remediate: "Remediate",
      leiCompliance: "LEI Compliance",
      registry: "Registry",
      manageRegistry: "Manage Registry",
      exposureTitle: "Market Exposure vs. AML Risk",
      exposureSubtitle: "Aggregated forensic signal analysis.",
      sentiment: "SENTIMENT",
      amlFlag: "AML FLAG",
      providerHealth: "Provider Health",
      response: "response",
      gatewayManagement: "Gateway Management"
    }
  },
  fr: {
    nav: {
      mission: "Contrôle de Mission",
      useCases: "Cas d'Usage",
      intelligence: "Hub d'Intelligence",
      verification: "APIs de Vérification",
      nodes: "Nœuds Globaux",
      contact: "Contact",
      signIn: "Connexion",
      getStarted: "Démarrer"
    },
    hero: {
      tagline: "Plateforme Mondiale de RegTech & d'Intelligence des Risques",
      title1: "Sécurisé. Souverain.",
      title2: "Intelligent.",
      desc: "Minerva Regtech unifie la vérification d'identité, l'intelligence d'entreprise, la criminalistique blockchain et le risque opérationnel dans un écosystème modulaire de classe entreprise.",
      init: "Initialiser la Plateforme",
      contactHub: "Contacter le Hub d'Intégration"
    },
    narrative: {
      title: "RegTech Mondiale",
      subtitle: "& Cyber Intelligence.",
      desc: "Minerva RegTech™ est une société de technologie réglementaire axée sur le renforcement de la conformité, la prévention de la criminalité financière et l'intégrité institutionnelle sur les marchés financiers et juridiques mondiaux.",
      unifiedStack: "Stack Unifiée",
      unifiedStackDesc: "Identité, Criminalistique et surveillance ESG dans un portail modulaire unique.",
      instantScale: "Échelle Instantanée",
      instantScaleDesc: "Nœuds natifs du cloud conçus pour un débit institutionnel."
    },
    portfolio: {
      title: "Notre Portfolio — Approuvé par les Institutions Mondiales"
    },
    risk: {
      tagline: "Paysage des Risques 2024",
      title: "Surveillance de Conformité à 360°.",
      desc: "Les institutions font aujourd'hui face à une pression croissante due à l'évolution des sanctions, aux menaces d'identité synthétique et aux structures complexes de bénéficiaires effectifs (UBO) mondiaux.",
      kyc: "KYC/KYB Transfrontalier",
      kycDesc: "Défis complexes de conformité multijuridictionnelle gérés à la source.",
      ubo: "UBO & Actionnariat",
      uboDesc: "Démasquer les structures complexes de bénéficiaires effectifs et les sociétés écrans opaques.",
      sanctions: "Sanctions Dynamiques",
      sanctionsDesc: "Cadres en évolution rapide nécessitant une synchronisation des listes en temps réel.",
      crypto: "AML Crypto-actifs",
      cryptoDesc: "Visibilité totale sur l'exposition aux actifs virtuels et les rampes d'accès à haut risque.",
      esg: "Obligations ESG",
      esgDesc: "Automatisation du reporting de durabilité obligatoire et surveillance des médias indésirables.",
      fraud: "Fraude en Temps Réel",
      fraudDesc: "Contrer le SIM-swap, l'identité synthétique et les menaces de fuite d'identifiants.",
      audit: "Surveillance & Audit",
      auditDesc: "Extension des exigences de surveillance institutionnelle et des audits des régulateurs.",
      sentiment: "Sentiment & Géopolitique",
      sentimentDesc: "Gestion des risques impactant les décisions opérationnelles et la réputation de la marque."
    },
    about: {
      protocol: "Le Protocole Minerva Regtech",
      title: "Vision & Mission.",
      desc1: "Minerva RegTech™ est une société de technologie réglementaire axée sur le renforcement de la conformité, la prévention de la criminalité financière et l'intégrité institutionnelle sur les marchés financiers et juridiques mondiaux.",
      desc2: "À mesure que les attentes réglementaires évoluent et que les écosystèmes financiers deviennent plus interconnectés, les organisations ont besoin de cadres de conformité intelligents, évolutifs et défendables.",
      cta: "Contrôle de Mission Complet",
      partner: "Votre meilleur partenaire",
      returnHome: "Retour à l'Accueil",
      missionControl: "Contrôle de Mission",
      origin: "Auckland, Nouvelle-Zélande",
      standard: "Le Standard Institutionnel",
      secureSovereign: "Sécurisé. Souverain. Intelligent.",
      standardDesc1: "Nous travaillons avec des sociétés Fintech, des banques, des sociétés de capital-investissement et de capital-risque, des participants au marché boursier, des régulateurs, des cabinets d'avocats et des family offices pour relever des défis réglementaires complexes avec clarté et confiance.",
      standardDesc2: "Nos solutions intègrent une vérification d'identité avancée, des technologies de lutte contre la criminalité financière et des systèmes de conformité efficaces conçus pour protéger les opérations tout en soutenant une croissance durable.",
      statHq: "Sièges",
      statNetwork: "Réseau",
      statNodes: "14 Nœuds Souverains",
      statIntel: "Intelligence",
      statNeural: "Vérification Neurale IA",
      statCompliance: "Conformité",
      statStandard: "Standard AML4/5D",
      architecture: "L'Architecture",
      pillars: "Piliers Stratégiques.",
      pillar1Title: "Stack Unifiée",
      pillar1Desc: "Unification de la vérification d'identité, de l'intelligence d'entreprise et du risque ESG dans un portail modulaire unique pour une surveillance institutionnelle instantanée.",
      pillar2Title: "Criminalistique en Temps Réel",
      pillar2Desc: "Surveillance continue de la blockchain et détection des fuites sur le darkweb garantissant que votre périmètre est défendu contre les crimes financiers émergents.",
      pillar3Title: "Données Souveraines",
      pillar3Desc: "Nœuds de résidence des données régionaux sur 5 continents, garantissant la conformité multijuridictionnelle et une souveraineté absolue des données.",
      ourMission: "Notre Mission",
      ourMissionDesc: "À mesure que les attentes réglementaires évoluent et que les écosystèmes financiers deviennent plus interconnectés, les organisations ont besoin de cadres de conformité intelligents, évolutifs et défendables. Minerva RegTech™ fournit des solutions technologiques qui renforcent la transparence, atténuent les risques et renforcent la confiance.",
      ourVision: "Notre Vision",
      ourVisionDesc: "Pionnier d'un avenir où la conformité réglementaire est un avantage stratégique. Nous envisageons un écosystème financier mondial où l'innovation et la croissance ne compromettent jamais l'intégrité, la sécurité ou la responsabilité sociale.",
      originBadge: "Ingénierie de Conformité Mondiale • Origine Nouvelle-Zélande",
      originDesc: "Minerva RegTech est constituée à Auckland et maintient des nœuds opérationnels à Londres, Bangkok, New York et Lomé.",
      initPlatform: "Initialiser la Plateforme",
      rights: "© 2023 Minerva Regtech"
    },
    services: {
      title: "Nœuds d'Intelligence.",
      desc: "Les stacks de conformité traditionnelles sont fragmentées et coûteuses. Minerva Regtech unifie l'identité, l'intelligence d'entreprise, la criminalistique et la vérification opérationnelle dans un portail unique.",
      ip: "Intelligence IP",
      ipDesc: "Profilage IP en temps réel avec géolocalisation, intelligence ASN et détection de proxy/VPN/TOR pour identifier la fraude.",
      kyc: "e-KYC / KYB",
      kycDesc: "Cartographie automatisée des UBO et validation structurelle des registres.",
      aml: "Sentinelle AML",
      amlDesc: "Filtrage des sanctions et surveillance des transactions par rapport aux listes de surveillance mondiales en temps réel.",
      lei: "Registre d'Identifiant d'Entité Juridique",
      leiDesc: "Gestion, émission et renouvellement des LEI conformes à la GLEIF via notre nœud dédié.",
      blockchain: "Intelligence Blockchain",
      blockchainDesc: "Notation des risques en profondeur et analyse des nexux de portefeuilles alimentée par Blockchain Intelligence™.",
      esg: "Due Diligence ESG",
      esgDesc: "Intelligence de durabilité et suivi des médias indésirables.",
      market: "Sentiment du Marché",
      marketDesc: "Surveillance de la perception sociale et détection des anomalies dans les actualités.",
      darkweb: "Surveillance du Darkweb",
      darkwebDesc: "Analyse 24/7 des nœuds onion pour les identifiants fuyés et les fuites de données d'entreprise.",
      explore: "Explorer le Module"
    },
    useCases: {
      tagline: "Applications Industrielles",
      title: "Conçu pour les Enjeux Élevés.",
      desc: "Des conglomérats bancaires mondiaux aux unités spécialisées d'application de la loi, Minerva fournit les nœuds d'intelligence nécessaires pour naviguer dans les paysages réglementaires complexes.",
      banking: "Banque & Finance",
      regulators: "Régulateurs & Force de l'ordre",
      fintech: "FinTech & DeFi",
      pe: "Private Equity & Capital Risque",
      cta: "Voir tous les Cas d'Usage",
      returnHome: "Retour à l'Accueil",
      titlePage: "Cas d'Usage",
      institutionalApps: "Applications Institutionnelles",
      industrySolutions: "Solutions Industrielles.",
      solutionsDesc: "Minerva RegTech™ fournit des nœuds d'intelligence spécialisés adaptés aux environnements financiers et réglementaires les plus exigeants au monde.",
      ready: "Prêt à déployer ?",
      readyDesc: "Rejoignez les institutions qui utilisent déjà Minerva Regtech pour sécuriser leurs opérations et automatiser la conformité.",
      init: "Initialiser la Plateforme",
      banks: "Banques",
      banksDesc: "Détection de fraude via Threat Intelligence, filtrage des sanctions en temps réel, surveillance AML/CTF, Due Diligence ESG (y compris la chaîne d'approvisionnement), intelligence opérationnelle (téléphone, IBAN, email), vérification des antécédents RH/employés, surveillance IP/Threat Intelligence et surveillance du Darkweb.",
      pevc: "Private Equity & Capital Risque (PE/VC)",
      pevcDesc: "Cartographie des UBO, Due Diligence ESG (y compris la chaîne d'approvisionnement), Intelligence Blockchain, analyse des sentiments du marché et des risques géopolitiques, tableaux de bord de conformité, vérification des dirigeants, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour l'évaluation du portefeuille.",
      law: "Application de la Loi & Régulateurs",
      lawDesc: "Application des sanctions transfrontalières, enquêtes médico-légales, intelligence d'entreprise, surveillance du marché, vérification des employés, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour la vérification des suspects.",
      stocks: "Marchés Boursiers & Bourses",
      stocksDesc: "Surveillance en temps réel des participants, surveillance des transactions, analyse des sentiments du marché, vérification de la conformité, Intelligence Blockchain, vérification des employés, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour les dirigeants des sociétés cotées.",
      fintechDefi: "Plateformes FinTech & DeFi",
      fintechDefiDesc: "Vérification de l'onboarding, AML des crypto-actifs, notation des risques des portefeuilles, filtrage sanctions/PEP, Due Diligence ESG (y compris la chaîne d'approvisionnement), intelligence opérationnelle (téléphone, IBAN, email), vérification des employés, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour les utilisateurs et partenaires.",
      familyOffices: "Family Offices",
      familyOfficesDesc: "Cartographie des UBO/actionnariat, surveillance des médias indésirables, Due Diligence ESG (y compris la chaîne d'approvisionnement), intelligence du marché et géopolitique, vérification des contreparties, vérification des employés, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour la surveillance du portefeuille.",
      riskIntel: "Intelligence des Risques de Marché, Réglementaires & Géopolitiques",
      riskIntelDesc: "Surveillance des sentiments sociaux et géopolitiques, alertes précoces, recoupement de l'intelligence d'entreprise, Intelligence Blockchain, signaux ESG, intelligence opérationnelle, surveillance IP/Threat Intelligence, surveillance du Darkweb et eKYC des réseaux sociaux pour un reporting d'intelligence exploitable."
    },
    verification: {
      tagline: "Passerelle Temps Réel",
      title: "Micro-services de Vérification.",
      desc: "APIs modulaires pour une vérification instantanée des contacts et financière, conçues pour être intégrées dans n'importe quel flux d'onboarding en quelques minutes.",
      iban: "Validation IBAN",
      ibanDesc: "Confirmation du Bénéficiaire (CoP) et accessibilité SEPA.",
      phone: "Intelligence Téléphonique",
      phoneDesc: "Détection de SIM-swap et audit global des signaux SS7.",
      email: "Criminalistique de Boîte Mail",
      emailDesc: "Vérification du handshake SMTP et contrôles de fuites DLP.",
      status: "Opérationnel",
      cta: "Demander des Clés API"
    },
    reach: {
      tagline: "Réseau Juridictionnel",
      title: "Infrastructure Mondiale.",
      desc: "Résidence des données localisée et nœuds de reporting haute vitesse à travers 14 régions souveraines et 5 continents."
    },
    contact: {
      successTitle: "Requête Transmise.",
      successDesc: "Un responsable de la conformité institutionnelle a été alerté. Le délai de réponse moyen est de 240 secondes.",
      successCta: "Nouvelle Transmission",
      tagline: "Support d'Intégration Sécurisé",
      title: "Contactez-nous.",
      desc: "Demandez un accès bêta privé ou un hébergement de nœud dédié.",
      name: "Nom Complet",
      email: "Email Professionnel",
      message: "Message",
      messagePlaceholder: "Besoins, région d'intérêt...",
      cta: "Envoi Sécurisé",
      hq: "Siège Social",
      returnHome: "Retour à l'Accueil",
      hub: "Hub",
      restricted: "Protocole d'Accès Restreint",
      calibration: "Calibration du Portail.",
      handshake: "La Poignée de Main.",
      demoDesc: "Notre portail d'intelligence institutionnelle est actuellement en cours de calibration réglementaire finale. Pour explorer toutes nos capacités ou demander un accès prioritaire, veuillez réserver une démo institutionnelle ci-dessous.",
      contactDesc: "Demandez un accès bêta privé, un déploiement à l'échelle de l'institution ou un hébergement de nœud dédié.",
      lead: "Responsable Institutionnel (Nom)",
      org: "Organisation",
      objective: "Objectif",
      demo: "Demander une Démo Produit",
      enterprise: "Mise en Œuvre Entreprise",
      quota: "Expansion du Quota API",
      partnership: "Partenariat Régulateur",
      dossier: "Bref du Dossier",
      dossierPlaceholder: "Précisez les besoins, la région d'intérêt et la résidence des données cible...",
      initBooking: "Initialiser la Réservation",
      globalInfra: "Infrastructure Mondiale",
      priority: "Ligne Prioritaire",
      priorityDesc: "Les failles de sécurité institutionnelles ou les nexux de sanctions urgents nécessitent un envoi direct.",
      verifyRegistry: "Vérifier le Registre"
    },
    footer: {
      desc: "Infrastructure sécurisée, transparente et performante pour la conformité réglementaire institutionnelle.",
      resources: "Ressources",
      mission: "Notre Mission",
      useCases: "Cas d'Usage",
      support: "Support Contact",
      register: "Créer un Compte",
      connect: "Connecter",
      rights: "© 2024 Minerva Regtech. Tous droits réservés.",
      privacy: "Politique de Confidentialité"
    },
    sidebar: {
      dashboard: "Tableau de Bord",
      regulatory: "Réglementaire (AML/LEI)",
      aml: "Surveillance AML",
      lei: "Registre LEI",
      kyc: "KYC Individuel",
      kyb: "KYB (Entreprise)",
      fintech: "Intelligence Fintech",
      blockchain: "Intelligence Blockchain",
      iban: "Surveillance IBAN",
      market: "Marché Boursier",
      intelligence: "Services d'Intelligence",
      esg: "Score de Risque ESG",
      darkweb: "Moniteur Darkweb",
      sentiment: "Sentiment du Marché",
      verification: "APIs de Vérification",
      phone: "Intelligence Téléphonique",
      email: "Validation d'Email",
      support: "Support",
      contact: "Hub de Contact",
      settings: "Paramètres",
      logout: "Déconnexion",
      plan: "Plan"
    },
    dashboard: {
      searchPlaceholder: "Rechercher des entités, ISINs ou LEIs...",
      officer: "Officier",
      admin: "Admin",
      role: "Conformité Senior",
      title: "Tableau de Bord d'Intelligence",
      subtitle: "Télémétrie des risques et de la conformité en temps réel.",
      apiUsage: "Utilisation de l'API",
      auditExport: "Exportation d'Audit",
      marketMonitor: "Moniteur de Marché",
      activeIsins: "ISIN Actifs",
      viewTerminal: "Voir le Terminal",
      blockchainIntel: "Intel Blockchain",
      addresses: "Adresses",
      forensics: "Criminalistique",
      darkwebLeak: "Fuite Darkweb",
      leaksFound: "Fuites Trouvées",
      remediate: "Remédier",
      leiCompliance: "Conformité LEI",
      registry: "Registre",
      manageRegistry: "Gérer le Registre",
      exposureTitle: "Exposition au Marché vs Risque AML",
      exposureSubtitle: "Analyse agrégée des signaux médico-légaux.",
      sentiment: "SENTIMENT",
      amlFlag: "DRAPEAU AML",
      providerHealth: "Santé du Fournisseur",
      response: "réponse",
      gatewayManagement: "Gestion de la Passerelle"
    }
  }
};
